import { GET_LIST } from 'react-admin'

import dataProvider from '../providers/dataProvider'
import { getResource } from './resources'
import { CURRENT_SEASON as FALLBACK_SEASON } from './variables'

const STORAGE_KEY = 'currentSeasonId'
const STORAGE_KEY_DAY = 'currentSeasonResolvedOn'

// Mes (0-11) en el que arranca una temporada nueva. De julio en adelante ya
// hablamos de la temporada que empieza este año natural.
const SEASON_START_MONTH = 6

let cachedSeasonId = null

const today = () => new Date().toISOString().slice(0, 10)

const readStoredSeasonId = () => {
  const stored = parseInt(localStorage.getItem(STORAGE_KEY), 10)
  return isNaN(stored) ? null : stored
}

const toTime = (date) => {
  const time = date ? new Date(date).getTime() : NaN
  return isNaN(time) ? null : time
}

// Año natural en el que arranca la temporada en curso: 2026 para la 2026/2027.
const getSeasonStartYear = (now) => (now.getMonth() >= SEASON_START_MONTH ? now.getFullYear() : now.getFullYear() - 1)

// Primer año de 4 dígitos que aparece en la temporada. "Temporada 2026/2027" -> 2026
const getSeasonYear = (season) => {
  const found = [season.name, season.season, season.year]
    .map((value) => String(value === null || value === undefined ? '' : value).match(/\d{4}/))
    .find(Boolean)
  return found ? parseInt(found[0], 10) : null
}

/**
 * La temporada en curso es la que contiene la fecha de hoy. Si las fechas no
 * están informadas todavía (pasa con la temporada recién creada) caemos en el
 * año de la propia temporada, y como último recurso en la última que ha empezado.
 */
const pickCurrentSeason = (seasons, now = new Date()) => {
  const todayTime = now.getTime()

  const inProgress = seasons.find((season) => {
    const start = toTime(season.start)
    const end = toTime(season.end)
    return start !== null && end !== null && start <= todayTime && todayTime <= end
  })
  if (inProgress) return inProgress

  const byYear = seasons.find((season) => getSeasonYear(season) === getSeasonStartYear(now))
  if (byYear) return byYear

  const started = seasons
    .filter((season) => toTime(season.start) !== null && toTime(season.start) <= todayTime)
    .sort((a, b) => toTime(b.start) - toTime(a.start))

  return started[0] || null
}

/**
 * Id de la temporada en curso. Sincrono: devuelve el valor ya resuelto (o el
 * cacheado de la sesión anterior) y, mientras no haya ninguno, el de config/variables.
 */
export const getCurrentSeason = () => cachedSeasonId || readStoredSeasonId() || FALLBACK_SEASON

/**
 * ¿Se ha resuelto ya hoy? Si no, conviene esperar a resolveCurrentSeason() antes
 * de pintar los listados: el cambio de temporada se detecta en la misma carga.
 */
export const isCurrentSeasonFresh = () => cachedSeasonId !== null || localStorage.getItem(STORAGE_KEY_DAY) === today()

/**
 * Recalcula la temporada en curso contra el servicio de competiciones y la cachea.
 * Se lanza al montar el layout, es decir, una vez el usuario está autenticado.
 */
export const resolveCurrentSeason = () =>
  dataProvider(GET_LIST, getResource('competiciones', 'seasons'), {
    // El servicio rechaza rangos de más de 100 elementos; ordenadas por id DESC
    // la temporada en curso siempre entra en la primera página.
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'id', order: 'DESC' },
    filter: { showInWeb: 1 },
  })
    .then(({ data }) => {
      const season = pickCurrentSeason(data || [])
      if (season) {
        cachedSeasonId = season.id
        localStorage.setItem(STORAGE_KEY, season.id)
        localStorage.setItem(STORAGE_KEY_DAY, today())
      }
      return getCurrentSeason()
    })
    .catch((error) => {
      console.error('No se ha podido resolver la temporada en curso', error)
      return getCurrentSeason()
    })

export { pickCurrentSeason }
export default getCurrentSeason
